import { scrollToHeader } from "./../scrollToTop";
import { UseType, VehicleClass } from "./../types/form";
import {
  Coverage,
  coveragePlans,
  InsuranceCoverage,
  tableInitialValues,
  tableVariables,
  IVA,
  paymentInitialValues,
  deductiblesInitialValues,
} from "./../types/tableConstants";
import { useContext, useEffect, useState } from "react";
import InsuranceService from "../Service/service";
import { FormContext } from "../FormContextProvider";
import useBasicModal from "../BasicModal/useBasicModal";
import { scrollToTop } from "../scrollToTop";

const usePlanComparison = () => {
  const variables = tableVariables;
  const service = new InsuranceService();
  const { setViewedPlans, customerData, vehicleData, customerId, rate, amis } =
    useContext(FormContext);
  const [coverage, setCoverage] = useState<string[]>([]);
  const [sendingMail, setSendingMail] = useState(false);
  const [basic, setBasic] = useState(tableInitialValues);
  const [premium, setPremium] = useState(tableInitialValues);
  const [basicPayment, setBasicPayment] = useState(paymentInitialValues);
  const [premiumPayment, setPremiumPayment] = useState(paymentInitialValues);
  const [loading, setLoading] = useState(true);
  const { show, setShow, setText, text } = useBasicModal();
  const [defaultsSet, setDefaultsSet] = useState(false);
  const [deductibles, setDeductibles] = useState(deductiblesInitialValues);

  const paymentCalc = {
    annual: {
      surcharge: 1,
      divisor: 1,
    },
    semestral: {
      surcharge: 1.053,
      divisor: 2,
    },
    trimonthly: {
      surcharge: 1.08,
      divisor: 4,
    },
    monthly: {
      surcharge: 1.099,
      divisor: 12,
    },
  };

  const [fixed, setFixed] = useState({
    DM: 0,
    RT: 0,
    RP: 0,
    RV: 0,
    RA: 0,
  });

  const handleValueChange = (event: any, name: string, isBasic: boolean) => {
    setLoading(true);
    isBasic
      ? setBasic({ ...basic, [name]: +event.target.value })
      : setPremium({ ...premium, [name]: +event.target.value });
  };

  const hasCoverage = (short: string, plan: Coverage) => {
    return getCoverage(plan)[short as keyof InsuranceCoverage];
  };

  const getCoverage = (plan: Coverage) => {
    const vehicleClass =
      vehicleData.class === VehicleClass.BIKE ? VehicleClass.BIKE : "default";
    return coveragePlans[vehicleData.type as keyof typeof coveragePlans][plan][
      vehicleClass
    ];
  };

  const setDefaults = (
    coverage: Coverage,
    plan: typeof tableInitialValues,
    setDefaultPlan: any
  ) => {
    setDefaultsSet(false);
    let data: any = {};
    const fixedKeys = Object.keys(fixed);
    Object.keys(plan).forEach((key) => {
      if (hasCoverage(key, coverage) && !fixedKeys.includes(key)) {
        const variable: any = variables[key];
        data[key] = variable[vehicleData.currency]
          ? variable[vehicleData.currency][0]
          : variable.days
          ? 7 // default value for days
          : true;

        if (key === "RCO") {
          data[key] =
            vehicleData.type === UseType.COMMERCIAL ? 10000000 : 5000000;
        }
      } else if (hasCoverage(key, coverage) && fixedKeys.includes(key)) {
        data[key] = fixed[key as keyof typeof fixed];
      }
    });
    setDefaultPlan(data);
    setDefaultsSet(true);
  };

  const insureMyCarHandler = () => {
    const url =
      "https://forms.zohopublic.com/sabatertrenard/form/AsegurarmiVehculoAutogestin/formperma/ix-lCNcVeiDuIHHcFbm9Yk0Rto9Ui5J_fUicY9MymnU";
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openWhatsApp = () => {
    const url = process.env.REACT_APP_WP || "";
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const getPayment = (
    type: "monthly" | "annual" | "trimonthly" | "semestral",
    netAmount: number
  ) => {
    const surcharge = paymentCalc[type].surcharge;
    const subtotal = (netAmount * surcharge) / paymentCalc[type].divisor;
    return subtotal + subtotal * IVA;
  };

  const setCalcPayments = (annual: number, payment: any) => {
    const setPayment = payment;
    setPayment({
      annual: getPayment("annual", annual),
      semestral: getPayment("semestral", annual),
      trimonthly: getPayment("trimonthly", annual),
      monthly: getPayment("monthly", annual),
    });
  };

  const sendEmailHandler = () => {
    setSendingMail(true);
    const vehicleClass =
      vehicleData.class === VehicleClass.PRIVATE
        ? "Particular"
        : vehicleData.class;
    service
      .sendEmail(customerId, vehicleData.currency, vehicleClass)
      .then((res: any) => {
        scrollToHeader();
        scrollToTop();
        if (res.data.statusCode === 200) {
          setText("Cotización enviada.\nEstamos a sus órdenes!");
        } else {
          setText(
            "Ups!\nHubo un error.\nPor favor reintente o solicite conversar con un agente."
          );
        }
        setShow(true);
      })
      .catch((err) => {
        setText(
          "Ups!\nHubo un error.\nPor favor reintente o solicite conversar con un agente."
        );
        setShow(true);
      })
      .finally(() => {
        setSendingMail(false);
      });
  };

  const handleClose = () => {
    setShow(false);
  };

  const setPayments = () => {
    setLoading(true);
    const payload = {
      rate: rate,
      amis: amis,
      vehicle: vehicleData,
      quotation: {
        basic: basic,
        premium: premium,
      },
    };

    service
      .getQuotation(payload)
      .then((res) => {
        if (res.data && res.data.body && !res.data.body.error) {
          const data = res.data.body;
          const basicRes = data.basic;
          const premiumRes = data.premium;
          const deductibles = data.deductibles;
          setDeductibles(deductibles);
          if (basicRes.data) {
            setCalcPayments(Number(basicRes.data.primaNeta), setBasicPayment);

            let payload: any = {
              customer: customerData,
              vehicle: vehicleData,
              basic: {
                quotationNumber: basicRes.id,
                primaNeta: basicRes.data.primaNeta,
                deductibles: {
                  DM: 0,
                  RC: deductibles.RCP,
                  RT: 0,
                },
                coverage: basic,
              },

              deductibles: deductibles,
            };

            if (premiumRes) {
              setCalcPayments(
                Number(premiumRes.data.primaNeta),
                setPremiumPayment
              );
              payload.premium = {
                quotationNumber: premiumRes.id,
                primaNeta: premiumRes.data.primaNeta,
                deductibles: {
                  DM: deductibles.DM,
                  RC: deductibles.RCP,
                  RT: deductibles.RT,
                },
                coverage: premium,
              };
            }

            service
              .udpateClientData(customerId, payload)
              .finally(() => setLoading(false));
          }
        } else if (res.data && res.data.body && res.data.body.error) {
          setText("2Hubo un problema al calcular la cotización");
          setShow(true);
        }
      })
      .catch(() => {
        setText("3Hubo un problema al calcular la cotización");
        setShow(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    scrollToTop();
    setViewedPlans(true);
    setFixed({
      RT: vehicleData.insuranceAmount,
      RA: vehicleData.insuranceAmount,
      DM: vehicleData.insuranceAmount,
      RV: vehicleData.insuranceAmount * 0.1,
      RP: vehicleData.insuranceAmount * 0.1,
    });

    const coverageList = getCoverage(Coverage.CDF);
    setCoverage(
      Object.keys(coverageList).filter(
        (key) => coverageList[key as keyof InsuranceCoverage]
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDefaults(Coverage.BASIC, basic, setBasic);
    setDefaults(Coverage.CDF, premium, setPremium);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixed]);

  useEffect(() => {
    const defaultValues = (values: any) => {
      return Object.values(values).every((value) => value !== 0);
    };

    if (defaultsSet && defaultValues(basic) && defaultValues(premium)) {
      setPayments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultsSet, basic, premium]);

  return {
    insureMyCarHandler,
    handleValueChange,
    sendEmailHandler,
    premiumPayment,
    openWhatsApp,
    basicPayment,
    handleClose,
    hasCoverage,
    sendingMail,
    deductibles,
    variables,
    coverage,
    loading,
    premium,
    basic,
    fixed,
    show,
    text,
  };
};

export default usePlanComparison;
